import useToast from "@repo/core/components/core/Toast";
import { useFidelityProposalStore } from "../../store";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { MobileProposalResource } from "@repo/core/services/resources/mobile-proposal";
import { apiInstance } from "@repo/core/services/instances/api";
import {
  mapToFidelityProposalForm,
  mapFidelityProposalToSavePayload
} from "./mappers";
import { withResolvedClientName } from "@repo/core/utils/proposalClientName.utils";
import { FidelityProposalFormType } from "@repo/core/stores/proposalForm/types";
import { buildRejectedFidelityProposalAlterPayload } from "@repo/core/screens/Proposals/services/effectuateRejectedFidelityProposal.service";
import { useProposalStore } from "@repo/core/stores/proposalStore";

const proposalsResource = new ProposalsResource(apiInstance);
const mobileProposalResource = new MobileProposalResource(apiInstance);

export const useFidelityProposal = () => {
  const fidelityProposalStore = useFidelityProposalStore();
  const toast = useToast();

  const getProposalData = async (lngProposta: string) => {
    try {
      const [mobileProposalResponse, proposalDetailsResponse] =
        await Promise.all([
          mobileProposalResource.getCompleteProposal(lngProposta),
          proposalsResource.getProposal(lngProposta)
        ]);

      if (!mobileProposalResponse.data) {
        throw new Error("No data received from proposal endpoint");
      }

      const mobileProposal = withResolvedClientName(mobileProposalResponse.data);
      const detailName = proposalDetailsResponse.data.strNome?.trim();
      const mergedWithLimitReassessment = {
        ...mobileProposal,
        strNome: mobileProposal.strNome?.trim() || detailName || "",
        strReavaliacaoLimite:
          proposalDetailsResponse.data.strReavaliacaoLimite ||
          mobileProposal.strReavaliacaoLimite
      };

      fidelityProposalStore.setProposalData(mergedWithLimitReassessment);
      return mergedWithLimitReassessment;
    } catch (error) {
      toast.error(
        "Erro ao buscar dados da proposta",
        "Não foi possível carregar os dados da proposta. Tente novamente."
      );
      throw error;
    }
  };

  const updateProposalData = (
    data: Partial<
      import("@repo/core/services/entities/Proposals").ConsultProposalResponseDTO
    >
  ) => {
    fidelityProposalStore.updateProposalData(data);
  };

  const getFidelityProposalForm = (): FidelityProposalFormType | null => {
    if (!fidelityProposalStore.proposalData) {
      return null;
    }
    return mapToFidelityProposalForm(fidelityProposalStore.proposalData);
  };

  const saveFidelityProposal = async () => {
    if (!fidelityProposalStore.proposalData) {
      toast.error(
        "Erro ao salvar proposta",
        "Não há dados da proposta para salvar."
      );
      throw new Error("No proposal data available to save");
    }

    try {
      const payload = mapFidelityProposalToSavePayload(
        fidelityProposalStore.proposalData
      );

      const response = await proposalsResource.editProposal(payload);

      if (response.data) {
        toast.success(
          "Proposta salva",
          "A proposta de fidelidade foi salva com sucesso."
        );

        return response.data;
      }

      throw new Error("No data received from alter proposal endpoint");
    } catch (error: unknown) {
      const errorMessage =
        (error instanceof Error ? error.message : undefined) ||
        "Não foi possível salvar a proposta. Tente novamente.";
      toast.error("Erro ao salvar proposta", errorMessage);
      throw error;
    }
  };

  const effectuateFidelityProposal = async () => {
    if (!fidelityProposalStore.proposalData?.lngProposta) {
      toast.error(
        "Erro ao efetivar proposta",
        "Não há dados da proposta para efetivar."
      );
      throw new Error("No proposal data available to effectuate");
    }

    const lngProposta = String(fidelityProposalStore.proposalData.lngProposta);
    const payload = buildRejectedFidelityProposalAlterPayload(
      fidelityProposalStore.proposalData
    );

    await proposalsResource.editProposal(payload);

    return proposalsResource.effectuateProposalWithBody(lngProposta);
  };

  const clearFidelityProposal = () => {
    fidelityProposalStore.resetFidelityProposalStore();
    useProposalStore.getState().setIsFidelityProposal(true);
  };

  return {
    ...fidelityProposalStore,
    getProposalData,
    updateProposalData,
    getFidelityProposalForm,
    saveFidelityProposal,
    effectuateFidelityProposal,
    clearFidelityProposal
  };
};
